import React from 'react';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { BillingAPI } from '../../../api';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { formatCurrency } from '../../../lib/currency';
import { prettyLocalDate } from '../../../lib/time';
import { makeStyles } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/DeleteForever';
import Alert from '@material-ui/lab/Alert';
import Button from '../../../components/Button';
import Dialog from '@material-ui/core/Dialog';
import {default as StdButton} from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import MatchingFeesForm from './MatchingFeesForm';
import Confirmation from '../../../components/Confirmation';
import moment from 'moment';


const defaultValues = {
  description: '',
  amount_excl: '',
  due_date: moment(),
  repeats: false,
  repeat_count: 6,
};

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  pullRight: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  delete: {
    marginLeft: '2rem',
    color: theme.palette.action.disabled,
    '&:hover': {
      color: theme.palette.action.active,
    },
  },
  button: {
    margin: '1rem',
  },
}));

const MatchingFees = ({providerUuid}) => {
  const [items, setItems] = useState();
  const [busy, setBusy] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [itemValues, setItemValues] = useState(defaultValues);
  const [itemErrors, setItemErrors] = useState({});
  const [deleteItem, setDeleteItem] = useState();
  const [deleteBusy, setDeleteBusy] = useState(false);
  const classes = useStyles();

  const tableHeader = () => (
    <TableRow>
      <TableCell>Due Date</TableCell>
      <TableCell>Description</TableCell>
      <TableCell align="right">Amount Incl</TableCell>
      <TableCell></TableCell>
    </TableRow>
  );

  const tableRow = (item) => (
    <TableRow key={item.id}>
      <TableCell>
        {prettyLocalDate(item.due_date)}
      </TableCell>
      <TableCell>
        {item.description}
      </TableCell>
      <TableCell align="right">
        {formatCurrency(item.amount.incl)}
      </TableCell>
      <TableCell>
        {<DeleteIcon className={classes.delete} onClick={()=>setDeleteItem(item)}/>}
      </TableCell>
    </TableRow>
  );

  const getInvoiceItems = async () => {
    try {
      const response = await BillingAPI.getInvoiceItems(providerUuid);
      setItems(response.payload.items);
    } catch {
      // console.log('Unable to load');  // TODO: something useful
    }
  };

  const deleteInvoiceItem = async() => {
    try {
      setDeleteBusy(true);
      const response = await BillingAPI.deleteInvoiceItem(providerUuid, deleteItem.id);
      setItems(response.payload.items);
    } catch {
      // console.log('Error deleting');  // TODO: something useful
    } finally {
      setDeleteBusy(false);
      setDeleteItem();
    }
  };

  const validateForm = (values) => {
    const errors = {};
    values.description = values.description.trim();
    if (values.description.length === 0) {
      errors.description = 'Description cannot be empty.';
    }
    values.amount_excl = formatCurrency(values.amount_excl, '');
    if (values.amount_excl === '0.00') {
      errors.amount_excl = 'Amount cannot be zero.';
    }
    if (values.repeats) {
      values.repeat_count = parseInt(values.repeat_count);
      if (isNaN(values.repeat_count)) {
        values.repeat_count = 0;
      }
      if (values.repeat_count <= 1) {
        errors.repeat_count = 'Repeat count must be greater than 1';
      }
    }
    setItemValues(values);
    setItemErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const addInvoiceItem = async() => {
    const values = {...itemValues};
    if (!validateForm(values)) return;
    setBusy(true);
    const due_date = values.due_date.format('YYYY-MM-DD');
    const repeat_count = values.repeats ? values.repeat_count : 1;  // 1 means only single transaction
    const response = await BillingAPI.createInvoiceItem(providerUuid, {...values, repeat_count, due_date});
    setItems(response.payload.items);
    setShowAddDialog(false);
    setBusy(false);
  };

  useEffect(() => {
    getInvoiceItems();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={classes.container}>
      <div className={classes.pullRight}>
        <Button label="Add" pointer="plus" className={classes.button} onClick={()=>{setShowAddDialog(true); setItemErrors({});}}/>
      </div>
      {items &&
        <Table>
          <TableHead>
            {tableHeader()}
          </TableHead>
          <TableBody>
            {items.map(item => tableRow(item))}
          </TableBody>
        </Table>
      }
      {items && items.length === 0 &&
        <Alert severity="info">No future matching fees found</Alert>
      }
      <Dialog open={showAddDialog} onClose={()=>setShowAddDialog(false)} aria-labelledby="retry-dialog-title">
        <DialogTitle id="retry-dialog-title">Add Matching Fees Payments</DialogTitle>
        <DialogContent>
          <MatchingFeesForm values={itemValues} errors={itemErrors} onChange={setItemValues}/>
        </DialogContent>
        <DialogActions>
          <StdButton onClick={()=>setShowAddDialog(false)} color="primary">
            Cancel
          </StdButton>
          <Button onClick={addInvoiceItem} variant="contained" label="Add Fees" color="primary" busy={busy}/>
        </DialogActions>
      </Dialog>
      {deleteItem &&
        <Confirmation
          onCancel={() => setDeleteItem()}
          onConfirm={() => deleteInvoiceItem()}
          title="Delete Matching Fee"
          message="Are you sure you want to delete this matching fee?"
          busy={deleteBusy}
          action="Delete"
        />
      }
    </div>
  );
};

MatchingFees.propTypes = {
  providerUuid: PropTypes.string.isRequired,
};

export default MatchingFees;
